import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import './claims.css';
import type { ClaimSummary } from './types';
import {
  dontBill,
  getClaim,
  getClaimSync,
  getModeSync,
  getQueueSync,
  getScenarioSync,
  getSettingsSync,
  getWaitingClaimsSync,
  getWaitingCountSync,
  getWaitingTotalSync,
  holdClaim,
  markMilestoneShown,
  restoreClaim,
  setDecisionChosen,
  unholdClaim,
  useClaimsVersion,
} from './store';
import { TRAINING_GOAL, compareSummaries } from './utils';
import { CompactBand } from '@/components/CompactBand';
import { Header } from './components/Header';
import { Queue } from './components/Queue';
import { Detail, type DetailHandle, type LearnEdit } from './components/Detail';
import { ConfirmedState } from './components/ConfirmedState';
import { AutopilotSummary } from './components/AutopilotSummary';
import { HoldDialog, DontBillDialog, LeaveDialog, MilestoneDialog } from './components/dialogs';
import { MILESTONE_STATS } from './store';
import { confirmClaim } from './store';
import { PMS } from '@/features/shared/pms';

interface Toast {
  id: number;
  msg: string;
  undoLabel?: string;
  onUndo?: () => void;
}

type Dialog =
  | { type: 'hold'; prefill?: { reason?: string; note?: string }; decisionOptionId?: string }
  | { type: 'dontbill' }
  | { type: 'leave'; patient: string; target: string }
  | { type: 'milestone' }
  | null;

function useIsDesktop(): boolean {
  const [d, setD] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true,
  );
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const h = () => { setD(m.matches); };
    m.addEventListener('change', h);
    return () => { m.removeEventListener('change', h); };
  }, []);
  return d;
}

function orderedIds(queue: ClaimSummary[]): string[] {
  const groups: ClaimSummary['group'][] = ['needs_decision', 'ready', 'waiting'];
  const out: string[] = [];
  for (const g of groups) {
    const rows = queue.filter((r) => r.group === g).sort(compareSummaries);
    out.push(...rows.map((r) => r.id));
  }
  return out;
}

export function ClaimsReview({ claimId }: { claimId?: string }) {
  useClaimsVersion();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const mode = getModeSync();
  const settings = getSettingsSync();
  const scenario = getScenarioSync();
  const queue = getQueueSync();
  const waitingCount = getWaitingCountSync();
  const waitingTotal = getWaitingTotalSync();

  const [loaded, setLoaded] = useState(scenario === '' ? false : false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [confirming, setConfirming] = useState(false);
  const [writebackFailedId, setWritebackFailedId] = useState<string | null>(null);
  const [confirmedInfo, setConfirmedInfo] = useState<{ claimId: string; confirmed: number; learn: LearnEdit | null } | null>(null);
  const [detailDirty, setDetailDirty] = useState(false);
  const detailRef = useRef<DetailHandle>(null);
  const toastSeq = useRef(0);

  // initial 400ms skeleton (once per mount)
  useEffect(() => {
    const t = setTimeout(() => { setLoaded(true); }, 400);
    return () => { clearTimeout(t); };
  }, []);

  // append "opened by" history + mark opened when a claim is selected
  useEffect(() => {
    if (claimId) void getClaim(claimId);
  }, [claimId]);

  // clear confirmed view when navigating to a different claim
  useEffect(() => {
    if (confirmedInfo && confirmedInfo.claimId !== claimId) setConfirmedInfo(null);
  }, [claimId, confirmedInfo]);

  // training, no claim, desktop → open the first claim
  useEffect(() => {
    if (loaded && mode === 'training' && !claimId && isDesktop && queue.length > 0) {
      const ids = orderedIds(queue);
      if (ids[0]) void navigate(`/claims/${ids[0]}`, { replace: true });
    }
  }, [loaded, mode, claimId, isDesktop, queue, navigate]);

  // beforeunload while dirty
  useEffect(() => {
    if (!detailDirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', h);
    return () => { window.removeEventListener('beforeunload', h); };
  }, [detailDirty]);

  const addToast = (msg: string, undo?: { label: string; onUndo: () => void }) => {
    const id = ++toastSeq.current;
    setToasts((t) => [...t, { id, msg, ...(undo ? { undoLabel: undo.label, onUndo: undo.onUndo } : {}) }]);
    setTimeout(() => { setToasts((t) => t.filter((x) => x.id !== id)); }, 8000);
  };

  // ---- selection with dirty guard ----
  const goTo = (id: string) => { void navigate(`/claims/${id}`); };
  const handleSelect = (id: string) => {
    if (id === claimId) return;
    if (detailRef.current?.isDirty()) {
      const patient = getClaimSync(claimId ?? '')?.patient ?? 'this claim';
      setDialog({ type: 'leave', patient, target: id });
      return;
    }
    goTo(id);
  };

  // ---- confirm ----
  const runConfirm = async (learn: LearnEdit | null) => {
    if (!claimId) return;
    setConfirming(true);
    const res = await confirmClaim(claimId);
    setConfirming(false);
    if (res.ok) {
      setWritebackFailedId(null);
      setDetailDirty(false);
      setConfirmedInfo({ claimId, confirmed: res.confirmed ?? settings.confirmed, learn });
      if (mode === 'training' && res.confirmed === TRAINING_GOAL && !settings.milestoneShown) {
        setTimeout(() => { setDialog({ type: 'milestone' }); }, 50);
      }
    } else {
      setWritebackFailedId(claimId);
    }
  };

  // ---- hold ----
  const openHold = (prefill?: { reason?: string; note?: string }, decisionOptionId?: string) =>
    { setDialog({ type: 'hold', ...(prefill ? { prefill } : {}), ...(decisionOptionId ? { decisionOptionId } : {}) }); };

  const doHold = async (v: { reason: string; note?: string; until?: string }) => {
    if (!claimId) return;
    const d = dialog?.type === 'hold' ? dialog : null;
    await holdClaim(claimId, v);
    if (d?.decisionOptionId) setDecisionChosen(claimId, d.decisionOptionId);
    const patient = getClaimSync(claimId)?.patient ?? 'Claim';
    setDialog(null);
    addToast(`${patient} is on hold.`, { label: 'Undo', onUndo: () => void unholdClaim(claimId) });
  };

  const doTakeOffHold = () => {
    if (claimId) void unholdClaim(claimId);
  };

  // ---- don't bill ----
  const doDontBill = async (v: { reason: string; other?: string }) => {
    if (!claimId) return;
    const patient = getClaimSync(claimId)?.patient ?? 'Claim';
    const remaining = orderedIds(getQueueSync()).filter((id) => id !== claimId);
    await dontBill(claimId, v);
    setDialog(null);
    setDetailDirty(false);
    addToast(`${patient} won’t be billed.`, { label: 'Undo', onUndo: () => void restoreClaim(claimId) });
    if (remaining[0]) goTo(remaining[0]);
    else void navigate('/claims');
  };

  // ---- milestone ----
  // D133: this page never turns autopilot on itself — the dialog's primary
  // action links to /settings/autopilot, the one place that switch lives.
  const keepReviewing = () => {
    markMilestoneShown();
    setDialog(null);
  };
  const openAutopilotSettings = () => {
    markMilestoneShown();
    setDialog(null);
  };

  // ---------- render ----------
  const mobileClass = !isDesktop ? (claimId ? 'clm-two-detail' : 'clm-two-queue') : '';

  if (!loaded) {
    return (
      <div className={`clm clm-page`}>
        <CompactBand loading />
        <div className="clm-body">
          <aside className="clm-queue" aria-label="Claims to review">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="clm-skel-row" />
            ))}
          </aside>
          <main className="clm-detail">
            <div className="clm-detail-inner">
              <div className="clm-skel" style={{ height: 60 }} />
              <div className="clm-skel" style={{ height: 160 }} />
              <div className="clm-skel" style={{ height: 160 }} />
            </div>
          </main>
        </div>
      </div>
    );
  }

  const readyRows = queue.filter((r) => r.group === 'ready');
  const readyCount = readyRows.length;
  const readyTotal = readyRows.reduce((s, r) => s + r.billed, 0);
  const needsCount = queue.filter((r) => r.group === 'needs_decision').length;

  const header = (
    <Header
      mode={mode}
      settings={settings}
      waitingCount={waitingCount}
      waitingTotal={waitingTotal}
      readyCount={readyCount}
      needsCount={needsCount}
      readyTotal={readyTotal}
      milestoneDismissed={settings.milestoneShown}
      onReopenMilestone={() => { setDialog({ type: 'milestone' }); }}
    />
  );

  // autopilot summary board
  if (mode === 'autopilot' && !claimId) {
    return (
      <div className="clm clm-page">
        {header}
        <AutopilotSummary waiting={getWaitingClaimsSync()} onReview={(id) => { goTo(id); }} />
        {renderDialogs()}
        {renderToasts()}
      </div>
    );
  }

  // empty queue (training)
  if (queue.length === 0 && !claimId) {
    return (
      <div className="clm clm-page">
        {header}
        <div className="clm-empty">
          <p className="clm-empty-title">Nothing to review.</p>
          <p className="clm-meta">New claims appear here when procedures are completed in {PMS}.</p>
        </div>
        {renderDialogs()}
        {renderToasts()}
      </div>
    );
  }

  const selectedClaim = claimId ? getClaimSync(claimId) : null;

  let detailArea: React.ReactNode = null;
  if (claimId) {
    if (confirmedInfo?.claimId === claimId && selectedClaim) {
      const next = orderedIds(getQueueSync())[0] ?? null;
      const nextSummary = next ? getQueueSync().find((r) => r.id === next) ?? null : null;
      detailArea = (
        <ConfirmedState
          claim={selectedClaim}
          mode={mode}
          confirmed={confirmedInfo.confirmed}
          learn={confirmedInfo.learn}
          next={nextSummary}
          onOpenNext={() => {
            setConfirmedInfo(null);
            if (next) goTo(next);
          }}
        />
      );
    } else if (claimId && !selectedClaim) {
      detailArea = <NotFoundInPage as="main" thing="claim" id={claimId} listLabel="claims to review" listTo="/claims" />;
    } else if (!selectedClaim || selectedClaim.removed) {
      detailArea = (
        <main className="clm-detail">
          <div className="clm-empty">
            <p className="clm-empty-title">This claim isn’t waiting for review.</p>
            <p className="clm-meta">It was confirmed or not billed. Open its record to see where it is.</p>
            <div style={{ marginTop: 14, display: 'flex', gap: 10, justifyContent: 'center' }}>
              <Link to={`/claims/record/${claimId}`} className="clm-btn clm-btn-sm">
                Open the claim record
              </Link>
              <button type="button" className="clm-btn clm-btn-sm clm-btn-primary" onClick={() => { void navigate('/claims'); }}>
                Back to claims
              </button>
            </div>
          </div>
        </main>
      );
    } else {
      detailArea = (
        <Detail
          key={selectedClaim.id}
          ref={detailRef}
          claim={selectedClaim}
          mode={mode}
          busy={confirming}
          confirming={confirming}
          writebackFailed={writebackFailedId === selectedClaim.id}
          onDirtyChange={setDetailDirty}
          onToast={(msg) => { addToast(msg); }}
          onConfirm={(learn) => { void runConfirm(learn); }}
          onOpenHold={openHold}
          onTakeOffHold={doTakeOffHold}
          onDontBill={() => { setDialog({ type: 'dontbill' }); }}
          {...(!isDesktop ? { onBack: () => { void navigate('/claims'); } } : {})}
        />
      );
    }
  }

  return (
    <div className={`clm clm-page ${mobileClass}`}>
      {header}
      <div className="clm-body">
        <Queue queue={queue} {...(claimId ? { selectedId: claimId } : {})} onSelect={handleSelect} />
        {detailArea}
      </div>
      {renderDialogs()}
      {renderToasts()}
    </div>
  );

  function renderDialogs() {
    if (!dialog) return null;
    if (dialog.type === 'hold') {
      return (
        <HoldDialog
          {...(dialog.prefill?.reason ? { prefillReason: dialog.prefill.reason } : {})}
          {...(dialog.prefill?.note ? { prefillNote: dialog.prefill.note } : {})}
          onCancel={() => { setDialog(null); }}
          onHold={(v) => { void doHold(v); }}
        />
      );
    }
    if (dialog.type === 'dontbill') {
      return <DontBillDialog onCancel={() => { setDialog(null); }} onConfirm={(v) => { void doDontBill(v); }} />;
    }
    if (dialog.type === 'leave') {
      return (
        <LeaveDialog
          patient={dialog.patient}
          onSave={() => {
            const target = dialog.target;
            void (async () => {
              await detailRef.current?.save();
              setDialog(null);
              goTo(target);
            })();
          }}
          onDiscard={() => {
            const target = dialog.target;
            setDetailDirty(false);
            setDialog(null);
            goTo(target);
          }}
          onCancel={() => { setDialog(null); }}
        />
      );
    }
    return (
      <MilestoneDialog changedCount={MILESTONE_STATS.changedCount} onKeepReviewing={keepReviewing} onOpenSettings={openAutopilotSettings} />
    );
  }

  function renderToasts() {
    if (toasts.length === 0) return null;
    return (
      <div className="clm-toasts">
        {toasts.map((t) => (
          <div key={t.id} className="clm-toast" role="status">
            <span>{t.msg}</span>
            {t.undoLabel ? (
              <button
                type="button"
                className="clm-toast-undo"
                onClick={() => {
                  t.onUndo?.();
                  setToasts((list) => list.filter((x) => x.id !== t.id));
                }}
              >
                {t.undoLabel}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    );
  }
}
